(() => {
if (/iPad/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
  document.documentElement.setAttribute('data-dd-ipad', '');
}
let externalPlayerId = 0;
class DDSplitHero extends HTMLElement {
  connectedCallback() {
    if (this.abortController && !this.abortController.signal.aborted) return;
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.isIntersecting = false;
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.externalPlayers = new Map();
    window.addEventListener('message', (event) => this.onExternalMessage(event), { signal });

    this.addEventListener('click', (event) => this.onClick(event), { signal });
    this.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
    this.querySelectorAll('[data-dd-split-video]').forEach((media) => {
      const iframe = media.querySelector('iframe[data-src]');
      if (iframe && media.dataset.ddExternalClick !== 'true') this.connectExternal(media, iframe, signal);
      const video = media.querySelector('video');
      if (!video) return;
      video.addEventListener('play', () => {
        if (!media.ddPlayRequest) video.dataset.ddDeliberatePlay = 'true';
      }, { signal });
      video.addEventListener('playing', () => {
        if (!this.isConnected || document.hidden || !this.isMediaVisible(media)) return this.pause(media);
        media.setAttribute('data-dd-video-ready', '');
        media.removeAttribute('data-dd-play-requested');
        this.setPlaying(media, true);
        if (video.dataset.ddDeliberatePlay === 'true') {
          delete video.dataset.ddDeliberatePlay;
          video.dispatchEvent(new CustomEvent('dd:media-play', { bubbles: true }));
        }
        this.hideControlsLater(media);
      }, { signal });
      for (const name of ['pause', 'ended']) video.addEventListener(name, () => this.setPlaying(media, false), { signal });
      video.addEventListener('error', () => {
        media.removeAttribute('data-dd-video-ready');
        this.pause(media);
      }, { signal });
      const poster = media.querySelector('.dd-split-hero__video-poster');
      poster?.addEventListener('load', () => this.prepareNativeControls(media), { signal });
      poster?.addEventListener('error', () => this.prepareNativeControls(media), { signal });
      for (const event of ['pointerdown', 'focusin']) video.addEventListener(event, () => {
        if (!video.controls) return;
        this.hydrateVideo(media);
        this.prepareNativeControls(media);
      }, { signal });
      if (video.autoplay && this.reducedMotionQuery.matches) {
        video.autoplay = false;
        video.pause();
        return;
      }
      // Native autoplay may begin while this deferred controller is upgrading.
      // Adopt that real state instead of waiting for an event that already fired.
      if (!video.paused && !video.ended) {
        media.setAttribute('data-dd-video-ready', '');
        this.setPlaying(media, true);
        this.hideControlsLater(media);
      }
    });
    this.querySelectorAll('details[data-dd-faq]').forEach((details) => {
      details.addEventListener('toggle', () => this.syncDisclosure(details), { signal });
      this.syncDisclosure(details);
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pauseAll();
      else this.resumeAutoplay();
    }, { signal });
    this.reducedMotionQuery.addEventListener('change', (event) => {
      if (event.matches) this.pauseAutoplay();
      else this.resumeAutoplay();
    }, { signal });
    window.addEventListener('resize', () => {
      this.scheduleDoubleSync();
      this.scheduleForegroundSeamSync();
      this.resumeAutoplay();
    }, { signal });

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === this) this.isIntersecting = entry.isIntersecting;
          else if (!entry.isIntersecting) this.pause(entry.target);
        }
        if (this.isIntersecting) this.resumeAutoplay();
        else this.pauseAll();
      }, { threshold: 0.1 });
      this.intersectionObserver.observe(this);
      this.querySelectorAll('[data-dd-split-video]').forEach((media) => this.intersectionObserver.observe(media));
    } else {
      this.isIntersecting = true;
      window.addEventListener('scroll', () => this.resumeAutoplay(), { passive: true, signal });
      this.resumeAutoplay();
    }

    if (typeof ResizeObserver !== 'undefined') {
      this.doubleResizeObserver = new ResizeObserver(() => this.scheduleDoubleSync());
      this.querySelectorAll('.dd-split-hero__double').forEach((double) => this.doubleResizeObserver.observe(double));
    }
    this.querySelectorAll('img.dd-split-hero__double-image, .dd-split-hero__double-photo').forEach((image) => {
      image.addEventListener('load', () => this.scheduleDoubleSync(), { signal });
    });
    this.scheduleDoubleSync();
    this.connectForegroundSeam(signal);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.intersectionObserver?.disconnect();
    this.doubleResizeObserver?.disconnect();
    if (this.doubleSyncFrame) cancelAnimationFrame(this.doubleSyncFrame);
    this.doubleSyncFrame = null;
    this.foregroundSeamObserver?.disconnect();
    this.foregroundSeamObserver = null;
    if (this.foregroundSeamFrame) cancelAnimationFrame(this.foregroundSeamFrame);
    this.foregroundSeamFrame = null;
    this.clearForegroundSeam();
    this.foregroundSeamMedia = null;
    this.foregroundSeamAperture = null;
    this.foregroundSeamImage = null;
    this.foregroundSeamQuery = null;
    this.pauseAll();
    this.querySelectorAll('[data-dd-external-click]').forEach((media) => clearTimeout(media.ddExternalClickTimer));
    this.externalPlayers?.forEach((state) => this.resetExternal(state, false));
    this.externalPlayers?.clear();
  }

  connectForegroundSeam(signal) {
    if (!this.hasAttribute('data-dd-mobile-foreground-seam')) return;
    this.foregroundSeamMedia = this.querySelector(':scope > .dd-split-hero__media');
    this.foregroundSeamAperture = this.foregroundSeamMedia?.querySelector(':scope > .dd-split-hero__layered');
    this.foregroundSeamImage = this.foregroundSeamAperture?.querySelector(':scope > .dd-split-hero__layer--foreground');
    if (!this.foregroundSeamImage) return;
    if (this.hasAttribute('data-dd-mobile-foreground-static')) {
      const syncFailure = () => this.toggleAttribute('data-dd-mobile-foreground-failed', this.foregroundSeamImage.complete && !this.foregroundSeamImage.naturalWidth);
      this.foregroundSeamImage.addEventListener('load', syncFailure, { signal });
      this.foregroundSeamImage.addEventListener('error', () => this.setAttribute('data-dd-mobile-foreground-failed', ''), { signal });
      syncFailure();
      return;
    }
    this.foregroundSeamImageFailed = false;
    this.foregroundSeamQuery = window.matchMedia('(max-width: 989px)');
    this.foregroundSeamQuery.addEventListener('change', () => this.scheduleForegroundSeamSync(), { signal });
    this.foregroundSeamImage.addEventListener('load', () => {
      this.foregroundSeamImageFailed = false;
      this.scheduleForegroundSeamSync();
    }, { signal });
    this.foregroundSeamImage.addEventListener('error', () => {
      this.foregroundSeamImageFailed = true;
      this.clearForegroundSeam();
    }, { signal });
    if (typeof ResizeObserver !== 'undefined') {
      this.foregroundSeamObserver = new ResizeObserver(() => this.scheduleForegroundSeamSync());
      for (const element of [this.foregroundSeamMedia, this.foregroundSeamAperture, this.foregroundSeamImage]) {
        this.foregroundSeamObserver.observe(element);
      }
    }
    this.scheduleForegroundSeamSync();
  }

  scheduleForegroundSeamSync() {
    if (this.hasAttribute('data-dd-mobile-foreground-static')) return;
    if (!this.foregroundSeamImage || this.foregroundSeamFrame) return;
    this.foregroundSeamFrame = requestAnimationFrame(() => {
      this.foregroundSeamFrame = null;
      this.syncForegroundSeam();
    });
  }

  clearForegroundSeam() {
    this.removeAttribute('data-dd-mobile-foreground-ready');
    this.style.removeProperty('--dd-mobile-foreground-top');
    this.style.removeProperty('--dd-mobile-foreground-clearance');
    this.style.removeProperty('--dd-mobile-foreground-media-height');
  }

  syncForegroundSeam() {
    const image = this.foregroundSeamImage;
    if (!this.isConnected || !this.foregroundSeamQuery?.matches || !image || this.foregroundSeamImageFailed || image.complete && !image.naturalWidth) {
      this.clearForegroundSeam();
      return;
    }
    let mediaRect = this.foregroundSeamMedia.getBoundingClientRect();
    let apertureRect = this.foregroundSeamAperture.getBoundingClientRect();
    let imageRect = image.getBoundingClientRect();
    const intrinsicWidth = image.naturalWidth || Number(image.getAttribute('width'));
    const intrinsicHeight = image.naturalHeight || Number(image.getAttribute('height'));
    if (mediaRect.height <= 0 || apertureRect.height <= 0 || imageRect.width <= 0 || imageRect.height <= 0 || intrinsicWidth <= 0 || intrinsicHeight <= 0) {
      this.clearForegroundSeam();
      return;
    }
    const configuredShare = Number(this.dataset.ddMobileForegroundShare);
    const share = Number.isFinite(configuredShare) ? Math.max(0.1, Math.min(0.9, configuredShare)) : 0.5;
    const compact = this.hasAttribute('data-dd-mobile-compact-overlap');
    if (compact && !this.hasAttribute('data-dd-mobile-foreground-ready')) {
      this.style.setProperty('--dd-mobile-foreground-media-height', `${mediaRect.height.toFixed(3)}px`);
      this.setAttribute('data-dd-mobile-foreground-ready', '');
      imageRect = image.getBoundingClientRect();
    }
    const paintedHeight = Math.min(imageRect.height, imageRect.width * intrinsicHeight / intrinsicWidth);
    if (compact) {
      const mediaHeight = `${(paintedHeight * (1 - share) + mediaRect.width * 0.14).toFixed(3)}px`;
      if (this.style.getPropertyValue('--dd-mobile-foreground-media-height') !== mediaHeight) {
        this.style.setProperty('--dd-mobile-foreground-media-height', mediaHeight);
      }
      mediaRect = this.foregroundSeamMedia.getBoundingClientRect();
      apertureRect = this.foregroundSeamAperture.getBoundingClientRect();
    }
    const top = mediaRect.bottom - apertureRect.top + (share - 0.5) * paintedHeight;
    this.style.setProperty('--dd-mobile-foreground-top', `${top.toFixed(3)}px`);
    this.style.setProperty('--dd-mobile-foreground-clearance', `${(paintedHeight * share).toFixed(3)}px`);
    this.setAttribute('data-dd-mobile-foreground-ready', '');
  }

  onClick(event) {
    const externalPoster = event.target.closest('[data-dd-split-external-poster]');
    if (externalPoster && this.contains(externalPoster)) {
      this.openExternalPoster(externalPoster);
      return;
    }
    const sound = event.target.closest('[data-dd-split-video-sound]');
    if (sound && this.contains(sound)) {
      const media = sound.closest('[data-dd-split-video]');
      if (media) this.toggleSound(media);
      return;
    }
    const button = event.target.closest('[data-dd-split-video-toggle]');
    if (!button || !this.contains(button)) return;

    const media = button.closest('[data-dd-split-video]');
    if (!media) return;

    if (media.hasAttribute('data-playing') || media.hasAttribute('data-dd-play-requested')) {
      if (media.dataset.ddVideoPlayback === 'autoplay') media.setAttribute('data-dd-autoplay-paused', '');
      this.pause(media);
    } else {
      media.removeAttribute('data-dd-autoplay-paused');
      this.play(media, true);
    }
  }

  openExternalPoster(poster) {
    const media = poster.closest('[data-dd-external-click]');
    const iframe = media?.querySelector('iframe[data-src]');
    if (!media || !iframe || poster.disabled) return;

    this.pauseAll();
    clearTimeout(media.ddExternalClickTimer);
    poster.disabled = true;
    poster.setAttribute('aria-busy', 'true');
    media.setAttribute('data-dd-external-click-loading', '');

    const revealPlayer = () => {
      if (!iframe.hasAttribute('src')) return;
      clearTimeout(media.ddExternalClickTimer);
      media.removeAttribute('data-dd-external-click-loading');
      media.setAttribute('data-dd-external-click-ready', '');
      poster.removeAttribute('aria-busy');
      iframe.removeAttribute('aria-hidden');
      iframe.removeAttribute('tabindex');
      requestAnimationFrame(() => iframe.focus({ preventScroll: true }));
    };

    iframe.addEventListener('load', revealPlayer, { once: true, signal: this.abortController.signal });
    iframe.src = iframe.dataset.src;
    media.ddExternalClickTimer = setTimeout(() => {
      if (media.hasAttribute('data-dd-external-click-ready')) return;
      iframe.removeAttribute('src');
      media.removeAttribute('data-dd-external-click-loading');
      poster.disabled = false;
      poster.removeAttribute('aria-busy');
    }, 12000);
  }

  onPointerUp(event) {
    if (event.pointerType !== 'touch' || event.target.closest('button, a')) return;
    const media = event.target.closest('[data-dd-split-video][data-playing]');
    if (media) this.revealControls(media);
  }

  play(media, deliberate = false) {
    if (media.dataset.ddVideoPlayback === 'off') return;

    this.querySelectorAll('[data-dd-split-video]').forEach((candidate) => {
      const simultaneousLayer = media.dataset.ddVideoLayer === 'layered' && candidate.dataset.ddVideoLayer === 'layered';
      if (candidate !== media && !simultaneousLayer) this.pause(candidate);
    });

    const video = media.querySelector('video');
    const iframe = media.querySelector('iframe[data-src]');
    if (!deliberate && (video ? video.paused : !media.hasAttribute('data-playing'))) media.dataset.ddUserUnmuted = 'false';
    if (video) {
      if (!deliberate && video.hasAttribute('data-dd-deferred-video')) {
        if (!this.isMediaVisible(media) || document.hidden || this.reducedMotionQuery?.matches) return;
        if (!video.querySelector('source[src]')) {
          if (media.ddStartRequest) return;
          const startRequest = {};
          media.ddStartRequest = startRequest;
          this.waitForVisibleImages().then(() => {
            if (media.ddStartRequest !== startRequest) return;
            media.ddStartRequest = null;
            if (!this.isConnected || document.hidden || this.reducedMotionQuery?.matches || !this.isMediaVisible(media) || media.hasAttribute('data-dd-autoplay-paused')) return;
            this.hydrateVideo(media);
            this.play(media);
          });
          return;
        }
      }
      media.ddStartRequest = null;
      this.hydrateVideo(media);
      if (media.dataset.ddUserUnmuted !== 'true') video.muted = true;
      if (deliberate) video.dataset.ddDeliberatePlay = 'true';
      const request = {};
      media.ddPlayRequest = request;
      media.setAttribute('data-dd-play-requested', '');
      const playPromise = video.play();
      if (playPromise) playPromise.then(() => {
        if (!this.isConnected || media.ddPlayRequest !== request) video.pause();
      }).catch(() => {
        if (media.ddPlayRequest !== request) return;
        media.removeAttribute('data-dd-play-requested');
        this.setPlaying(media, false);
      });
      return;
    }

    if (iframe) this.playExternal(this.externalPlayers.get(iframe), deliberate);
  }

  pause(media) {
    const pending = media.hasAttribute('data-dd-play-requested');
    media.ddStartRequest = null;
    media.ddPlayRequest = null;
    media.removeAttribute('data-dd-play-requested');
    media.querySelector('video')?.pause();
    const iframe = media.querySelector('iframe[src]');
    if (iframe) {
      const state = this.externalPlayers?.get(iframe);
      if (state) {
        state.requested = false;
        this.clearExternalWait(state);
        this.postPlayback(iframe, 'pause');
        if (pending) this.resetExternal(state, false);
      }
    }
    this.setPlaying(media, false);
  }

  connectExternal(media, iframe, signal) {
    const url = new URL(iframe.dataset.src, window.location.href);
    const provider = iframe.dataset.ddVideoProvider;
    if (provider === 'youtube' ? !['https://www.youtube-nocookie.com', 'https://www.youtube.com'].includes(url.origin) : provider !== 'vimeo' || url.origin !== 'https://player.vimeo.com') return;
    const state = { media, iframe, provider, origin: url.origin, ready: false, loaded: false, requested: false };
    this.externalPlayers.set(iframe, state);
    iframe.addEventListener('load', () => {
      if (!iframe.hasAttribute('src')) return;
      state.loaded = true;
      if (state.requested && !state.ready) this.probeExternal(state);
    }, { signal });
    iframe.addEventListener('error', () => this.resetExternal(state), { signal });
  }

  playExternal(state, deliberate = false) {
    if (!state || state.requested) return;
    const { media, iframe } = state;
    state.requested = true;
    state.deliberate = deliberate;
    state.acknowledged = false;
    media.setAttribute('data-dd-play-requested', '');
    media.querySelector('[data-dd-split-video-toggle]')?.setAttribute('aria-busy', 'true');
    this.setPlaying(media, false);
    if (!iframe.hasAttribute('src')) {
      state.id = `dd-split-${++externalPlayerId}`;
      state.ready = state.loaded = false;
      const url = new URL(iframe.dataset.src);
      url.searchParams.set('autoplay', '0');
      url.searchParams.set(state.provider === 'youtube' ? 'mute' : 'muted', '1');
      url.searchParams.set('controls', media.dataset.ddShowVideoControls === 'false' ? '0' : '1');
      if (state.provider === 'youtube') {
        url.searchParams.set('origin', window.location.origin);
        url.searchParams.set('widgetid', state.id);
      }
      iframe.style.opacity = '0';
      iframe.style.pointerEvents = 'none';
      iframe.setAttribute('aria-hidden', 'true');
      iframe.src = url.href;
    }
    state.timeout = setTimeout(() => this.resetExternal(state), 12000);
    if (state.ready) this.postPlayback(iframe, 'play');
    else {
      this.probeExternal(state);
      state.handshake = setInterval(() => this.probeExternal(state), 250);
    }
  }

  probeExternal(state) {
    if (!state.loaded || state.ready || !state.requested) return;
    this.postExternal(state, state.provider === 'youtube' ? { event: 'listening' } : { method: 'ping' });
  }

  onExternalMessage(event) {
    const state = [...this.externalPlayers.values()].find((entry) => entry.iframe.contentWindow === event.source && entry.origin === event.origin);
    if (!state || !state.loaded || !state.iframe.hasAttribute('src')) return;
    let data = event.data;
    try { if (typeof data === 'string') data = JSON.parse(data); } catch { return; }
    if (!data || typeof data !== 'object') return;
    const youtube = state.provider === 'youtube';
    if (youtube && String(data.id) !== state.id) return;
    if (data.event === 'onError' || data.event === 'onAutoplayBlocked' || data.event === 'error') return this.resetExternal(state);
    const ready = youtube ? ['onReady', 'initialDelivery'].includes(data.event) : data.event === 'ready' || data.method === 'ping';
    if (ready && !state.ready) {
      state.ready = true;
      clearInterval(state.handshake);
      const events = youtube ? ['onStateChange', 'onError', 'onAutoplayBlocked'] : ['playing', 'pause', 'ended', 'error'];
      events.forEach((name) => this.postExternal(state, youtube ? { event: 'command', func: 'addEventListener', args: [name] } : { method: 'addEventListener', value: name }));
      if (state.requested) this.postPlayback(state.iframe, 'play');
    }
    const playerState = youtube ? (data.event === 'onStateChange' ? data.info : data.event === 'infoDelivery' ? data.info?.playerState : undefined) : undefined;
    const playing = youtube ? playerState === 1 : data.event === 'playing';
    const stopped = youtube ? playerState === 0 || playerState === 2 : data.event === 'pause' || data.event === 'ended';
    if (playing) {
      if (!state.ready || !state.requested || !this.isConnected || document.hidden || !this.isIntersecting) return this.pause(state.media);
      state.acknowledged = true;
      this.clearExternalWait(state);
      state.media.querySelector('.dd-split-hero__external-poster')?.setAttribute('hidden', '');
      state.iframe.style.removeProperty('opacity');
      state.iframe.style.removeProperty('pointer-events');
      state.iframe.removeAttribute('aria-hidden');
      this.setPlaying(state.media, true);
      if (state.deliberate) {
        state.deliberate = false;
        state.iframe.dispatchEvent(new CustomEvent('dd:media-play', { bubbles: true }));
      }
      this.hideControlsLater(state.media);
    } else if (stopped && state.acknowledged) {
      state.requested = false;
      this.clearExternalWait(state);
      this.setPlaying(state.media, false);
    }
  }

  clearExternalWait(state) {
    clearTimeout(state.timeout);
    clearInterval(state.handshake);
    state.media.removeAttribute('data-dd-play-requested');
    state.media.querySelector('[data-dd-split-video-toggle]')?.removeAttribute('aria-busy');
  }

  resetExternal(state, holdAutoplay = true) {
    state.requested = state.ready = state.loaded = false;
    this.clearExternalWait(state);
    state.iframe.removeAttribute('src');
    state.media.querySelector('.dd-split-hero__external-poster')?.removeAttribute('hidden');
    if (holdAutoplay && state.media.dataset.ddVideoPlayback === 'autoplay') state.media.setAttribute('data-dd-autoplay-paused', '');
    this.setPlaying(state.media, false);
  }

  postExternal(state, message) {
    if (!state.iframe.hasAttribute('src')) return;
    if (state.provider === 'youtube') message = { ...message, id: state.id, channel: 'widget' };
    state.iframe.contentWindow?.postMessage(JSON.stringify(message), state.origin);
  }

  pauseAll() {
    this.querySelectorAll('[data-dd-split-video]').forEach((media) => this.pause(media));
  }

  pauseAutoplay() {
    this.querySelectorAll('[data-dd-video-playback="autoplay"]').forEach((media) => this.pause(media));
  }

  isMediaVisible(media) {
    const box = media.getBoundingClientRect();
    return box.width > 0 && box.height > 0 && box.bottom > 0 && box.right > 0
      && box.top < window.innerHeight && box.left < window.innerWidth
      && getComputedStyle(media).visibility !== 'hidden';
  }

  hydrateVideo(media) {
    const video = media.querySelector('video[data-dd-deferred-video]');
    if (!video) return;
    const poster = media.querySelector('.dd-split-hero__video-poster');
    if (poster?.complete && poster.naturalWidth && poster.currentSrc) video.poster = poster.currentSrc;
    let changed = false;
    video.querySelectorAll('source[data-dd-src]').forEach((source) => {
      if (source.hasAttribute('src')) return;
      source.src = source.dataset.ddSrc;
      changed = true;
    });
    if (changed) video.load();
  }

  prepareNativeControls(media) {
    const video = media.querySelector('video[data-dd-deferred-video]');
    if (!video?.controls || !this.isConnected || document.hidden || !this.isMediaVisible(media)) return;
    const poster = media.querySelector('.dd-split-hero__video-poster');
    if (poster && !poster.complete) return;
    this.hydrateVideo(media);
    video.style.opacity = '1';
    if (poster?.naturalWidth) poster.style.visibility = 'hidden';
  }

  waitForVisibleImages() {
    const signal = this.abortController.signal;
    const images = [...this.querySelectorAll('img:not([data-dd-fallback-src])')].filter((image) => this.isMediaVisible(image));
    return Promise.all(images.map((image) => new Promise((resolve) => {
      const finish = () => {
        image.removeEventListener('load', finish);
        image.removeEventListener('error', finish);
        signal.removeEventListener('abort', finish);
        resolve();
      };
      if (image.complete || signal.aborted) return finish();
      image.addEventListener('load', finish, { once: true });
      image.addEventListener('error', finish, { once: true });
      signal.addEventListener('abort', finish, { once: true });
    })));
  }

  resumeAutoplay() {
    if (!this.isIntersecting || document.hidden) return;
    this.querySelectorAll('[data-dd-split-video]').forEach((media) => {
      if (this.isMediaVisible(media)) this.prepareNativeControls(media);
      else this.pause(media);
    });
    if (this.reducedMotionQuery?.matches) return;
    this.querySelectorAll('[data-dd-video-playback="autoplay"]:not([data-dd-autoplay-paused])').forEach((media) => {
      if (this.isMediaVisible(media)) this.play(media);
    });
  }

  scheduleDoubleSync() {
    if (this.doubleSyncFrame) return;
    this.doubleSyncFrame = requestAnimationFrame(() => {
      this.doubleSyncFrame = null;
      this.syncDoubleMedia();
    });
  }

  syncRecipeDoubleMedia(double, canvas, group, images) {
    const mobileFit = window.matchMedia('(max-width: 989px)').matches
      && this.classList.contains('dd-split-hero--mobile-media-width-gutter')
      && this.classList.contains('dd-split-hero--mobile-media-size-fit')
      && double.hasAttribute('data-dd-mobile-fit');
    const fitKey = `${double.getBoundingClientRect().width.toFixed(3)}|${double.className}|${double.getAttribute('style')}`;
    if (mobileFit && double.dataset.ddSplitRecipeFitKey === fitKey) return;
    delete double.dataset.ddSplitFitWidth;
    delete double.dataset.ddSplitRecipeFitKey;
    double.style.removeProperty('--dd-split-double-fit-height');
    for (const property of ['--dd-split-double-fit-scale', '--dd-split-double-visible-shift-x', '--dd-split-double-visible-shift-y', '--dd-split-double-decoration-scale']) {
      group.style.removeProperty(property);
    }
    const canvasBounds = canvas.getBoundingClientRect();
    if (canvasBounds.width <= 1 || canvasBounds.height <= 1) return;
    const doubleStyle = getComputedStyle(double);
    const shadowX = Number.parseFloat(doubleStyle.getPropertyValue('--dd-split-double-shadow-x')) || 0;
    const shadowY = Number.parseFloat(doubleStyle.getPropertyValue('--dd-split-double-shadow-y')) || 0;
    const bounds = images.map((image) => {
      const rectangle = image.getBoundingClientRect();
      const transform = getComputedStyle(image).transform;
      const matrix = new DOMMatrix(transform === 'none' ? undefined : transform);
      const x = matrix.a * shadowX + matrix.c * shadowY;
      const y = matrix.b * shadowX + matrix.d * shadowY;
      return {
        left: rectangle.left - canvasBounds.left,
        right: rectangle.right - canvasBounds.left,
        top: rectangle.top - canvasBounds.top,
        bottom: rectangle.bottom - canvasBounds.top,
        shadowLeft: Math.min(0, x),
        shadowRight: Math.max(0, x),
        shadowTop: Math.min(0, y),
        shadowBottom: Math.max(0, y),
      };
    });
    if (bounds.some((box) => box.right <= box.left || box.bottom <= box.top)) return;
    let scale = mobileFit ? Infinity : 1;
    for (const end of bounds) {
      for (const start of bounds) {
        const width = end.right - start.left;
        if (width > 0) scale = Math.min(scale, (canvasBounds.width - 1 - end.shadowRight + start.shadowLeft) / width);
        const height = end.bottom - start.top;
        if (!mobileFit && height > 0) scale = Math.min(scale, (canvasBounds.height - 1 - end.shadowBottom + start.shadowTop) / height);
      }
    }
    if (!Number.isFinite(scale) || scale <= 0) return;
    const left = Math.min(...bounds.map((box) => box.left * scale + box.shadowLeft));
    const right = Math.max(...bounds.map((box) => box.right * scale + box.shadowRight));
    const top = Math.min(...bounds.map((box) => box.top * scale + box.shadowTop));
    const bottom = Math.max(...bounds.map((box) => box.bottom * scale + box.shadowBottom));
    group.style.setProperty('--dd-split-double-fit-scale', scale.toFixed(6));
    group.style.setProperty('--dd-split-double-decoration-scale', scale.toFixed(6));
    group.style.setProperty('--dd-split-double-visible-shift-x', `${((canvasBounds.width - right + left) / 2 - left).toFixed(3)}px`);
    group.style.setProperty('--dd-split-double-visible-shift-y', `${(mobileFit ? 0.5 - top : (canvasBounds.height - bottom + top) / 2 - top).toFixed(3)}px`);
    if (mobileFit) {
      double.style.setProperty('--dd-split-double-fit-height', `${Math.ceil(bottom - top + 1)}px`);
      double.dataset.ddSplitRecipeFitKey = `${double.getBoundingClientRect().width.toFixed(3)}|${double.className}|${double.getAttribute('style')}`;
    }
  }

  syncDoubleMedia() {
    this.querySelectorAll('.dd-split-hero__double').forEach((double) => {
      const canvas = double.querySelector('[data-dd-split-double-canvas]') || double;
      const group = double.querySelector('[data-dd-split-double-group]');
      const images = [...double.querySelectorAll('.dd-split-hero__double-image')];
      if (!group || images.length < 2) return;
      if (double.classList.contains('dd-split-hero__double--recipe')) {
        this.syncRecipeDoubleMedia(double, canvas, group, images);
        return;
      }
      delete double.dataset.ddSplitRecipeFitKey;
      group.style.removeProperty('--dd-split-double-decoration-scale');

      const mobileFit = window.matchMedia('(max-width: 989px)').matches
        && this.classList.contains('dd-split-hero--mobile-media-width-gutter')
        && this.classList.contains('dd-split-hero--mobile-media-size-fit')
        && double.hasAttribute('data-dd-mobile-fit');
      const fitWidth = double.getBoundingClientRect().width.toFixed(3);

      if (mobileFit && double.dataset.ddSplitFitWidth === fitWidth) return;

      if (!mobileFit) delete double.dataset.ddSplitFitWidth;
      double.style.removeProperty('--dd-split-double-fit-height');
      group.style.removeProperty('--dd-split-double-fit-scale');
      group.style.removeProperty('--dd-split-double-visible-shift-x');
      group.style.removeProperty('--dd-split-double-visible-shift-y');

      const canvasBounds = canvas.getBoundingClientRect();
      const groupBounds = group.getBoundingClientRect();
      if (canvasBounds.width <= 0 || canvasBounds.height <= 0 || groupBounds.width <= 0 || groupBounds.height <= 0) return;

      if (mobileFit) {
        group.style.setProperty('--dd-split-double-fit-scale', '1');
        group.style.setProperty('--dd-split-double-visible-shift-x', '0px');
        group.style.setProperty('--dd-split-double-visible-shift-y', '0px');

        const baseCanvas = canvas.getBoundingClientRect();
        const baseImages = images.map((image) => image.getBoundingClientRect());
        if (baseImages.some((bounds) => bounds.width <= 0 || bounds.height <= 0)) return;
        const union = baseImages.reduce((bounds, image) => ({
          left: Math.min(bounds.left, image.left),
          top: Math.min(bounds.top, image.top),
          right: Math.max(bounds.right, image.right),
          bottom: Math.max(bounds.bottom, image.bottom),
        }), baseImages[0]);
        const unionWidth = union.right - union.left;
        const unionHeight = union.bottom - union.top;
        if (unionWidth <= 0 || unionHeight <= 0) return;

        // A half-pixel inset protects rounded antialiasing while still filling
        // the available gutter width to the browser's rendered pixel.
        const fitInset = 0.5;
        const scale = (baseCanvas.width - fitInset * 2) / unionWidth;
        group.style.setProperty('--dd-split-double-fit-scale', scale.toFixed(6));
        group.style.setProperty('--dd-split-double-visible-shift-x', `${(fitInset - (union.left - baseCanvas.left) * scale).toFixed(3)}px`);
        group.style.setProperty('--dd-split-double-visible-shift-y', `${(fitInset - (union.top - baseCanvas.top) * scale).toFixed(3)}px`);
        double.style.setProperty('--dd-split-double-fit-height', `${Math.ceil(unionHeight * scale + fitInset * 2)}px`);
        double.dataset.ddSplitFitWidth = fitWidth;
        return;
      }

      const currentShiftX = Number.parseFloat(group.style.getPropertyValue('--dd-split-double-visible-shift-x')) || 0;
      const currentShiftY = Number.parseFloat(group.style.getPropertyValue('--dd-split-double-visible-shift-y')) || 0;

      const imageBounds = images.map((image) => {
        const bounds = image.getBoundingClientRect();
        return {
          left: bounds.left - canvasBounds.left,
          top: bounds.top - canvasBounds.top,
          right: bounds.right - canvasBounds.left,
          bottom: bounds.bottom - canvasBounds.top,
        };
      });
      if (imageBounds.some((bounds) => bounds.right <= bounds.left || bounds.bottom <= bounds.top)) return;
      const union = imageBounds.reduce((bounds, image) => ({
        left: Math.min(bounds.left, image.left),
        top: Math.min(bounds.top, image.top),
        right: Math.max(bounds.right, image.right),
        bottom: Math.max(bounds.bottom, image.bottom),
      }), imageBounds[0]);
      const baseUnion = {
        left: union.left - currentShiftX,
        top: union.top - currentShiftY,
        right: union.right - currentShiftX,
        bottom: union.bottom - currentShiftY,
      };
      const desiredX = canvasBounds.width / 2 - (baseUnion.left + baseUnion.right) / 2;
      const desiredY = canvasBounds.height / 2 - (baseUnion.top + baseUnion.bottom) / 2;
      const minX = -baseUnion.left;
      const maxX = canvasBounds.width - baseUnion.right;
      const minY = -baseUnion.top;
      const maxY = canvasBounds.height - baseUnion.bottom;
      const shiftX = minX <= maxX ? Math.min(maxX, Math.max(minX, desiredX)) : desiredX;
      const shiftY = minY <= maxY ? Math.min(maxY, Math.max(minY, desiredY)) : desiredY;

      group.style.setProperty('--dd-split-double-visible-shift-x', `${shiftX.toFixed(3)}px`);
      group.style.setProperty('--dd-split-double-visible-shift-y', `${shiftY.toFixed(3)}px`);
    });
  }

  setPlaying(media, playing) {
    media.toggleAttribute('data-playing', playing);
    const button = media.querySelector('[data-dd-split-video-toggle]');
    if (!button) return;
    button.setAttribute('aria-pressed', String(playing));
    button.setAttribute('aria-label', button.getAttribute('aria-label').replace(/^(Play|Pause)/, playing ? 'Pause' : 'Play'));
    if (!playing) media.setAttribute('data-dd-controls-visible', '');
    this.syncSound(media);
  }

  toggleSound(media) {
    const video = media.querySelector('video');
    const iframe = media.querySelector('iframe[src]');
    const unmuted = media.dataset.ddUserUnmuted !== 'true';
    media.dataset.ddUserUnmuted = String(unmuted);
    if (video) video.muted = !unmuted;
    if (iframe) {
      const state = this.externalPlayers?.get(iframe);
      if (state?.ready) this.postExternal(state, state.provider === 'youtube'
        ? { event: 'command', func: unmuted ? 'unMute' : 'mute', args: [] }
        : { method: 'setVolume', value: unmuted ? 1 : 0 });
    }
    this.syncSound(media);
    this.revealControls(media);
  }

  syncSound(media) {
    const button = media.querySelector('[data-dd-split-video-sound]');
    if (!button) return;
    const unmuted = media.dataset.ddUserUnmuted === 'true';
    button.setAttribute('aria-pressed', String(unmuted));
    button.setAttribute('aria-label', unmuted ? 'Mute video' : 'Unmute video');
    button.textContent = unmuted ? 'Sound on' : 'Sound off';
  }

  revealControls(media) {
    media.setAttribute('data-dd-controls-visible', '');
    this.hideControlsLater(media);
  }

  hideControlsLater(media) {
    clearTimeout(media.ddControlsTimer);
    media.ddControlsTimer = setTimeout(() => {
      if (media.hasAttribute('data-playing') && !media.matches(':focus-within')) media.removeAttribute('data-dd-controls-visible');
    }, 2200);
  }

  postPlayback(iframe, action) {
    const state = this.externalPlayers?.get(iframe);
    if (!state?.ready) return;
    if (action === 'play' && state.media.dataset.ddUserUnmuted !== 'true') this.postExternal(state, state.provider === 'youtube' ? { event: 'command', func: 'mute', args: [] } : { method: 'setVolume', value: 0 });
    this.postExternal(state, state.provider === 'youtube' ? { event: 'command', func: `${action}Video`, args: [] } : { method: action });
  }

  syncDisclosure(details) {
    details.querySelector('summary')?.setAttribute('aria-expanded', String(details.open));
  }
}

if (!customElements.get('dd-split-hero')) customElements.define('dd-split-hero', DDSplitHero);
})();
